/**
 * Which topic a config node subscribes to, and whether it stands on its own.
 *
 * Shared by `WidgetSlot` — the one place a topic is actually registered — and by the widgets
 * whose own `useWidgetData` call repeats the registration as a no-op. The two must agree
 * exactly: if they disagreed the widget would claim a *second* topic for the same component,
 * and that topic would go into the preventer ACL request as something nothing reads
 * (CLAUDE.md constraint 2). One function, called by both, is what makes that impossible.
 *
 * ## `absoluteTopic`
 *
 * `topic` is relative to `baseTopic` and always has been. `absoluteTopic` is a full broker
 * topic used verbatim, and it wins when both are present.
 *
 * It exists because `FloorOccupancyWidget`'s data is published to the tenant-wide
 * `B0004/T0000003/environment-statuses`, one level *above* the `B0004/T0000003/signage`
 * prefix every other topic hangs off — and a prefix that is concatenated cannot be climbed
 * out of. That topic belongs to a Node-RED flow shared with other teams' products and is not
 * ours to change, so the app reaches out to it instead (docs/MIGRATION.md decision #26).
 *
 * Nothing here is specific to that widget: any config node may carry the key.
 */

export type ResolvedWidgetTopic = {
  /** The topic to register, or `undefined` for a static widget. */
  topic: string | undefined;
  /** `true` when `topic` is a full broker topic and `baseTopic` must not be prefixed. */
  absolute: boolean;
};

/**
 * Widget options are unvalidated by design, so neither key can be assumed to exist or to be a
 * string — a numeric or object `topic` in a hand-edited config would otherwise reach a broker
 * ACL request as the literal `"[object Object]"`.
 */
export function resolveWidgetTopic(options: {
  topic?: unknown;
  absoluteTopic?: unknown;
}): ResolvedWidgetTopic {
  if (typeof options.absoluteTopic === 'string' && options.absoluteTopic !== '') {
    return { topic: options.absoluteTopic, absolute: true };
  }
  return {
    topic: typeof options.topic === 'string' ? options.topic : undefined,
    absolute: false,
  };
}
